import { motion } from 'framer-motion'
import { useInView } from 'framer-motion'
import { useRef, useEffect, useState } from 'react'

// ─── Optional animated counter ────────────────────────────────────────────────
function AnimatedNumber({ value, suffix = '' }) {
  const [display, setDisplay] = useState(0)
  const ref      = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-40px' })

  // Parse the numeric part (e.g. "500+" → 500, "10+" → 10, "100%" → 100)
  const numeric = parseInt(String(value).replace(/\D/g, ''), 10) || 0
  const tail    = String(value).replace(/[0-9]/g, '')   // e.g. "+", "%", "K"

  useEffect(() => {
    if (!isInView || numeric === 0) return
    let start     = 0
    const step    = Math.ceil(numeric / 40)
    const timer   = setInterval(() => {
      start += step
      if (start >= numeric) { setDisplay(numeric); clearInterval(timer) }
      else setDisplay(start)
    }, 30)
    return () => clearInterval(timer)
  }, [isInView, numeric])

  return (
    <span ref={ref}>
      {isInView ? display : 0}{tail}
    </span>
  )
}

/**
 * StatBadge — a centered stat block with an animated number and label.
 *
 * Props:
 *   number   — the statistic value, e.g. "500+", "10+", "100%"
 *   label    — descriptive label below the number
 *   animate  — enable counter animation (default: true)
 *   light    — white text for dark backgrounds (default: false)
 */
export default function StatBadge({
  number,
  label,
  animate = true,
  light   = false,
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="text-center"
    >
      {/* Big number — Fredoka One */}
      <p
        className={[
          'font-display text-4xl md:text-5xl leading-none mb-1',
          light ? 'text-white' : 'text-primary',
        ].join(' ')}
      >
        {animate ? <AnimatedNumber value={number} /> : number}
      </p>

      {/* Label — Nunito */}
      <p
        className={[
          'font-body text-sm mt-1.5',
          light ? 'text-white/70' : 'text-ink-muted',
        ].join(' ')}
      >
        {label}
      </p>
    </motion.div>
  )
}
